import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import StyleTile from './StyleTile';

/**
 * Everything that belongs to a pose, in one row.
 *
 * These were three separate sections -- the order, the background, and the photo
 * styles -- and setting up a pose meant declaring it in one, scrolling to pick its
 * background in another, then scrolling further to find its look. They are the same
 * subject: pose number is the join between a PackagePose (order + background) and a
 * LookTemplate (which declares the pose it renders to).
 *
 * Two things the layout has to respect, and they pull in opposite directions:
 *
 *   the order and the background are per PACKAGE  -- one value per pose
 *   the look is per LEVEL-SPLIT SEASON            -- a fall pose can have a
 *                                                    different look for HS/MS than
 *                                                    for Elementary
 *
 * So a row has one background cell and one order control, and a look cell that
 * lists each level. Duplicating the background per level would imply three values
 * where the package holds one.
 */
const isCollage = (look) => look && look.look_type === 'collage';

// Looks that render to this pose, one entry per level season.
const looksForPose = (styles, poseNumber) => styles.filter(
  (st) => st.pose_bound && String(st.pose_number) === String(poseNumber),
);

const PoseSetup = ({
  product, choices, commit, sample, busy, offered, onToggleLook, scopedVariantId, inheriting,
}) => {
  const [adding, setAdding] = useState('');
  const styles = choices.styles || [];
  const backgrounds = choices.backgrounds || [];
  const declared = (product.pose_order || []).map(String);
  // A fall product's looks come from level-split seasons, so the level has to be
  // named on each look; a single-season product would just repeat itself.
  const multiLevel = new Set(styles.map((st) => st.season)).size > 1;
  const backgroundFor = new Map(
    (product.pose_backgrounds || []).map((row) => [String(row.pose_number), row.background_id]),
  );

  // Every pose worth a row: the ones the package declares, plus any a look renders
  // to. A look for pose 7 makes pose 7 relevant even before anyone declares it.
  const fromLooks = [...new Set(styles.filter((st) => st.pose_bound).map((st) => String(st.pose_number)))];
  const undeclared = fromLooks.filter((n) => !declared.includes(n))
    .sort((a, b) => Number(a) - Number(b));

  // Every write carries the chosen choice (#5435). Pose order and backgrounds are
  // stored per legacy Option, and without this they landed on the pricelist-wide list
  // -- so adding a pose while one choice was selected added it to all of them.
  const scoped = (fields) => (scopedVariantId ? { ...fields, variant_id: scopedVariantId } : fields);
  const saveOrder = (list) => commit(scoped({ pose_order: list.join(', ') }));

  const move = (poseNumber, by) => {
    const index = declared.indexOf(poseNumber);
    const target = index + by;
    if (index === -1 || target < 0 || target >= declared.length) return;

    const next = [...declared];
    [next[index], next[target]] = [next[target], next[index]];
    saveOrder(next);
  };

  const lookCell = (poseNumber) => {
    const looks = looksForPose(styles, poseNumber);
    if (!looks.length) {
      return <Typography variant="caption" color="text.disabled">plain photo</Typography>;
    }

    // Laid out across rather than down, and each entry kept narrow: a fall pose
    // carries a look per level, and with the caption beside the tile one entry was
    // 270px wide, so three of them wrapped into a 280px-tall row.
    //
    // No checkbox either -- StyleTile already shows its state as a border and
    // toggles on click, so a checkbox next to it was a second control for one
    // choice.
    return (
      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
        {looks.map((look) => (
          <Box key={look.id} sx={{ width: 104 }}>
            <StyleTile
              style={look}
              sample={sample}
              on={offered.includes(look.id)}
              onClick={() => onToggleLook(look.id)}
            />
            <Typography variant="caption" color="text.secondary" display="block">
              {multiLevel ? `${look.level} · ` : ''}
              {look.look_type}
            </Typography>
            {/* Only one look can style a pose: published_for_image is an index_by
                over an UNORDERED query, so the last row returned wins. "currently"
                is doing real work -- touching an unrelated look's updated_at moves
                its row and flips the winner (demonstrated on #5239: FALL27-01 gave
                way to a gif_slide). So this reports what resolves NOW, not a
                setting anyone chose. */}
            {look.applied ? null : (
              <Typography variant="caption" color="warning.main" display="block">
                {`not used — ${look.applied_look_name || 'another look'} currently wins`}
              </Typography>
            )}
          </Box>
        ))}
      </Stack>
    );
  };

  // The background is ignored for a pose whose applied look is a collage: EC2 is
  // handed `background: nil` for one, so it would paint over the design.
  const backgroundNote = (poseNumber) => {
    const applied = looksForPose(styles, poseNumber).filter((look) => look.applied);
    if (!applied.length || !applied.some(isCollage)) return undefined;

    return applied.every(isCollage)
      ? 'Not used — the look has its own background'
      : `Not used for ${applied.filter(isCollage).map((l) => l.level).join(', ')}`;
  };

  const backgroundCell = (poseNumber) => (
    <TextField
      select
      size="small"
      value={backgroundFor.get(poseNumber) || ''}
      sx={{ minWidth: 150 }}
      disabled={busy}
      helperText={backgroundNote(poseNumber)}
      onChange={(e) => commit(scoped({ pose_backgrounds: { [poseNumber]: e.target.value } }))}
    >
      <MenuItem value="">— none —</MenuItem>
      {backgrounds.map((bg) => (
        <MenuItem key={bg.id} value={bg.id}>{bg.label}</MenuItem>
      ))}
    </TextField>
  );

  return (
    <Box>
      <Typography variant="subtitle2">Poses — order, background and look</Typography>
      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
        {'The order customers see them in, what each is composited on, and which look '
          + 'renders to it. A look declares the pose it belongs to, so it cannot be moved '
          + 'from here — the style designer owns that.'}
      </Typography>

      {/* Copy-on-write is invisible otherwise: the table looks the same before and
          after the first edit, and the difference — whether this choice follows the
          product from now on — is the whole point of the selector above. */}
      {inheriting ? (
        <Typography variant="caption" color="warning.main" display="block" gutterBottom>
          Showing the product&rsquo;s pose order. The first change here gives this choice its own
          copy; the others keep following the product.
        </Typography>
      ) : null}
      {scopedVariantId && !inheriting ? (
        <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
          This choice has its own pose order. Changes here affect no other choice.
        </Typography>
      ) : null}

      {/* Viewport-relative rather than a fixed 420px: a row is as tall as the pose
          has levels (a three-level fall pose is ~130px), so a fixed height showed
          two or three rows and left the editing inside a cramped scroller. Floored
          so a short window still shows a row properly, capped so a tall one does not
          turn the table into the whole page. */}
      <Box sx={{
        maxHeight: 'clamp(420px, 68vh, 900px)',
        overflowY: 'auto',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      }}
      >
        {/* Fixed layout, or the column widths below are only hints: with the
            default auto layout the background select's own width won the argument
            and squeezed the look cell to 339px -- 13px short of three tiles, so the
            third wrapped and every row doubled in height. */}
        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 56 }}>Pose</TableCell>
              <TableCell sx={{ width: 72 }}>Order</TableCell>
              <TableCell sx={{ width: 168 }}>Background</TableCell>
              <TableCell>Look</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {declared.map((poseNumber, index) => (
              <TableRow key={poseNumber}>
                <TableCell>{poseNumber}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    disabled={busy || index === 0}
                    onClick={() => move(poseNumber, -1)}
                  >
                    <ArrowUpwardIcon fontSize="inherit" />
                  </IconButton>
                  <IconButton
                    size="small"
                    disabled={busy || index === declared.length - 1}
                    onClick={() => move(poseNumber, 1)}
                  >
                    <ArrowDownwardIcon fontSize="inherit" />
                  </IconButton>
                </TableCell>
                <TableCell>{backgroundCell(poseNumber)}</TableCell>
                <TableCell>{lookCell(poseNumber)}</TableCell>
              </TableRow>
            ))}

            {/* A pose a look renders to but the package does not declare. It still
                reaches the customer -- an undeclared pose sorts after the declared
                ones by number -- so it is shown rather than hidden. */}
            {undeclared.map((poseNumber) => (
              <TableRow key={poseNumber}>
                <TableCell>{poseNumber}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    disabled={busy}
                    onClick={() => saveOrder([...declared, poseNumber])}
                  >
                    add
                  </Button>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.disabled">
                    not in the order yet
                  </Typography>
                </TableCell>
                <TableCell>{lookCell(poseNumber)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
        <TextField
          label="Add a pose"
          size="small"
          value={adding}
          placeholder="e.g. 12"
          sx={{ width: 130 }}
          onChange={(e) => setAdding(e.target.value)}
        />
        <Button
          sx={{ mt: 0.5 }}
          disabled={busy || !adding.trim()}
          onClick={() => {
            saveOrder([...declared, adding.trim()]);
            setAdding('');
          }}
        >
          Add
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block">
        {'Leaving a pose out does not hide it — the gallery shows undeclared poses after '
          + 'the declared ones, by number.'}
      </Typography>
    </Box>
  );
};

PoseSetup.propTypes = {
  product: PropTypes.shape({
    pose_order: PropTypes.arrayOf(PropTypes.string),
    pose_backgrounds: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  choices: PropTypes.shape({
    styles: PropTypes.arrayOf(PropTypes.object),
    backgrounds: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  commit: PropTypes.func.isRequired,
  sample: PropTypes.shape({ watermark_url: PropTypes.string }),
  busy: PropTypes.bool,
  // Which looks are offered, and the toggle: both belong to whichever package
  // the step is scoped to, so they are the parent's to own -- see Presentation.
  offered: PropTypes.arrayOf(PropTypes.number),
  onToggleLook: PropTypes.func.isRequired,
  // The choice these poses belong to, or null for the product-wide list (#5435).
  scopedVariantId: PropTypes.string,
  // True when the choice is still showing the product's list. The first edit gives it
  // a copy of its own (Shop::PackageBuilder#own_list_owner), which is worth saying out
  // loud before someone reorders what they think is the shared list.
  inheriting: PropTypes.bool,
};

PoseSetup.defaultProps = {
  sample: null, busy: false, offered: [], scopedVariantId: null, inheriting: false,
};

export default PoseSetup;
